import React, { useEffect, useState } from 'react'

import { RendererWrapper } from '@app/components/renderers'

import { SIZE } from '@app/constants'

import permissionService from '@app/services/permissionService'

import { useUserQuery } from '@app/hooks'

import { Role } from '@app/types'

import { LntSelect } from '@app/components/base'

import { DefaultOptionType } from 'antd/es/select'

import { ClassifierControlsDataSource } from '../../types'

import styles from '../../lib/input.module.scss'

import type {
  EditedData,
  EditedDataItem,
  UseClassifierControlColumnParams,
  UseClassifierControlEditableColumnReturn
} from './types'

const { hasOneOfRoles } = permissionService
const roles: Role[] = ['administrator', 'price_analytics_specialist']

// TODO: вынести в отдельный компонент для редактирования
const EditableSelectCell: React.FC<{
  isEditingRow: boolean
  canEdit: boolean
  options: DefaultOptionType[]
  defaultValue: string
  categoryNewValue?: string | null
  onValueChange: (newValue: string) => void
  MONITORING_CATEGORY_KEYS: Record<string, string>
}> = ({ canEdit, isEditingRow, options, defaultValue, onValueChange, categoryNewValue, MONITORING_CATEGORY_KEYS }) => {
  const [newValue, setNewValue] = useState<string>(defaultValue)

  const [hasChanged, setHasChanged] = useState(false)

  useEffect(() => {
    setHasChanged((MONITORING_CATEGORY_KEYS[newValue] || newValue) !== defaultValue)
    if (categoryNewValue && isEditingRow) {
      setNewValue(categoryNewValue)
    }

    if (hasChanged) {
      onValueChange(newValue)
    }
  }, [newValue, hasChanged, defaultValue, categoryNewValue, isEditingRow, MONITORING_CATEGORY_KEYS])

  const onChange = (e: string): void => {
    setNewValue(MONITORING_CATEGORY_KEYS[e])
  }

  if (!canEdit) {
    return <div>{ newValue }</div>
  }

  return (
    <LntSelect
      className={ `
          ${hasChanged ? styles.edited : ''}
          ${newValue === '' ? styles.edited : ''}
          ` }
      defaultValue={ defaultValue }
      value={ newValue }
      options={ options }
      onChange={ e => onChange(e) }
      size={ SIZE.small }
      allowClear={ false }
    />
  )
}

export const useClassifierControlEditableColumn = <DataSourceType extends ClassifierControlsDataSource>({
  columns,
  selectedIds,
  categoryNewValue,
  monitoringCategoriesData,
  MONITORING_CATEGORY_KEYS: categoryKeys
}: UseClassifierControlColumnParams<DataSourceType>): UseClassifierControlEditableColumnReturn<DataSourceType> => {
  const [columnsClassifierControl, setColumnsClassifierControl] = useState(columns)
  const [editedData, setEditedData] = useState<EditedData>([])

  const updateEditedData = (recordId: string, key: keyof EditedDataItem, newValue: string): any => {
    setEditedData(prevData => {
      const updatedData = [...prevData]
      const index = updatedData.findIndex(item => item.kindId === recordId)

      if (index > -1) {
        updatedData[index] = { ...updatedData[index], [key]: newValue }
      } else {
        updatedData.push({
          kindId: recordId,
          categoryId: '',
          [key]: newValue
        })
      }

      return updatedData
    })
  }
  // TODO: Вынести в отдельный хук проверку роли
  const { data: user } = useUserQuery()

  const canEdit = user?.roles ? hasOneOfRoles(user.roles, roles) : false

  useEffect(() => {
    const updatedColumns = columns.map(column => {
      if (column?.key === 'categoryId') {
        return {
          ...column,
          render: (_: any, record: { id: React.Key, categoryId: any }) => {
            const isEditingRow = selectedIds?.includes(record?.id) ?? false

            return (
              <RendererWrapper minWidth={ 200 }>
                <EditableSelectCell
                  onValueChange={ newValue => updateEditedData(record?.id as string, 'categoryId', newValue) }
                  isEditingRow={ isEditingRow }
                  canEdit={ canEdit }
                  options={ monitoringCategoriesData ?? [] }
                  defaultValue={ record?.categoryId ?? '' }
                  categoryNewValue={ categoryNewValue }
                  MONITORING_CATEGORY_KEYS={ categoryKeys }
                />
              </RendererWrapper>
            )
          }
        }
      }

      return column
    })

    setColumnsClassifierControl(updatedColumns)
  }, [columns, selectedIds, monitoringCategoriesData, canEdit, categoryNewValue])

  return { columnsClassifierControl, editedData, monitoringCategoriesData }
}
